import { useEffect, useState, useMemo } from "react";
import { Skeleton, Dropdown, Drawer } from "../../components/ui";
import dayjs from "dayjs";
import swal from "../../utils/swal";
import {
  Search,
  Filter,
  Plus,
  MoreHorizontal,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Check,
  School,
  SearchX,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  useGetAllClasses,
  useUpdateClassStatus,
  useDeleteClass,
} from "../../query/classQuery";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { classroomColorTone } from "../../config/classroomColors";
import DataBulkToolbar from "../../components/DataTable/DataBulkToolbar";
import DataTableCards from "../../components/DataTable/DataTableCards";
import { confirmBulkAction, runBulkIds, showBulkResult } from "../../utils/bulkActions";
import "../teachers/teachers.css";
import "./classroom.css";
import FilterSelect from "../../components/FilterSelect";

const CLASS_STATUSES = [
  { value: "ACTIVE", label: "Active" },
  { value: "INACTIVE", label: "Inactive" },
];

function statusTone(status) {
  return status === "ACTIVE" ? "is-active" : "is-inactive";
}

function statusLabel(status) {
  return status === "ACTIVE" ? "Active" : "Inactive";
}

function initials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase() || "C";
}

function teacherName(teacher) {
  if (!teacher) return "";
  return `${teacher.firstName || ""} ${teacher.lastName || ""}`.trim();
}

function pageWindow(current, totalPages) {
  if (totalPages <= 3) return Array.from({ length: totalPages }, (_, i) => i + 1);
  if (current <= 2) return [1, 2, 3];
  if (current >= totalPages - 1) return [totalPages - 2, totalPages - 1, totalPages];
  return [current - 1, current, current + 1];
}

function ClassManagement() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedSort, setSelectedSort] = useState("newest");
  const [selectedIds, setSelectedIds] = useState([]);
  const [paginationConfig, setPaginationConfig] = useState({
    pageNumber: 1,
    limit: 10,
    keyword: "",
    status: "",
    sort: "newest",
    totalDocs: 0,
  });
  const debouncedKeyword = useDebouncedValue(searchTerm);

  const { isLoading, isFetching, data: classes } = useGetAllClasses({
    page: String(paginationConfig.pageNumber),
    limit: String(paginationConfig.limit),
    keyword: paginationConfig.keyword,
    status: paginationConfig.status,
    sort: paginationConfig.sort,
  });
  const { mutate: updateClassStatus, mutateAsync: updateClassStatusAsync, isPending: isUpdatingStatus } =
    useUpdateClassStatus();
  const { mutate: deleteClass, mutateAsync: deleteClassAsync } = useDeleteClass();
  const [isBulkRunning, setIsBulkRunning] = useState(false);

  const docs = useMemo(() => classes?.docs || [], [classes]);
  const totalDocs = classes?.totalDocs || 0;
  const totalPages = Math.max(1, classes?.totalPages || 1);
  const currentPage = paginationConfig.pageNumber;
  const from = totalDocs ? (currentPage - 1) * paginationConfig.limit + 1 : 0;
  const to = Math.min(currentPage * paginationConfig.limit, totalDocs);
  const allSelected = docs.length > 0 && docs.every((row) => selectedIds.includes(row._id));
  const hasFilters = Boolean(paginationConfig.status || (paginationConfig.sort && paginationConfig.sort !== "newest"));
  const hasQuery = Boolean(paginationConfig.keyword || hasFilters);

  const applyFilter = () => {
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      status: selectedStatus || "",
      sort: selectedSort || "newest",
    }));
    setOpen(false);
  };

  const resetFilter = () => {
    setSelectedStatus("");
    setSelectedSort("newest");
    setPaginationConfig((prev) => ({
      ...prev,
      pageNumber: 1,
      status: "",
      sort: "newest",
    }));
    setOpen(false);
  };

  const handleStatus = (selected, status, current) => {
    if (!selected || status === current) return;
    updateClassStatus(
      { classId: selected, status },
      {
        onSuccess: () => swal("Success", `Status updated to ${statusLabel(status)}`, "success"),
        onError: (error) => swal("Error", error?.message || "Failed to update status", "error"),
      }
    );
  };

  const handleDelete = (item) => {
    if (item?.studentCount > 0) {
      swal(
        "Cannot delete",
        "This class still has students assigned. Reassign or remove the students first.",
        "warning"
      );
      return;
    }
    swal({
      title: "Are you sure?",
      text: "Do you want to delete this class?",
      icon: "warning",
      buttons: ["No, cancel it!", "Yes, I am sure!"],
      dangerMode: true,
    }).then((isConfirm) => {
      if (!isConfirm) return;
      deleteClass(item._id, {
        onSuccess: () => swal("Success", "Class deleted successfully", "success"),
        onError: (error) => swal("Cannot delete", error?.message || "Failed to delete class", "error"),
      });
    });
  };

  const handleBulkStatus = async (status) => {
    if (!selectedIds.length) return;
    const confirmed = await confirmBulkAction({
      actionLabel: `Mark as ${statusLabel(status).toLowerCase()}`,
      count: selectedIds.length,
      entityLabel: "classes",
    });
    if (!confirmed) return;

    setIsBulkRunning(true);
    const { success, failed } = await runBulkIds(selectedIds, (id) =>
      updateClassStatusAsync({ classId: id, status })
    );
    setSelectedIds([]);
    setIsBulkRunning(false);
    showBulkResult({ actionLabel: "Status update", success, failed });
  };

  const handleBulkDelete = async () => {
    if (!selectedIds.length) return;
    const confirmed = await confirmBulkAction({
      actionLabel: "Delete selected",
      count: selectedIds.length,
      entityLabel: "classes",
    });
    if (!confirmed) return;

    let skipped = 0;
    const deletableIds = [];
    selectedIds.forEach((id) => {
      const item = docs.find((row) => row._id === id);
      if (item?.studentCount > 0) {
        skipped += 1;
        return;
      }
      deletableIds.push(id);
    });

    if (!deletableIds.length) {
      swal("Cannot delete", "Selected classes still have students assigned.", "warning");
      return;
    }

    setIsBulkRunning(true);
    const { success, failed } = await runBulkIds(deletableIds, deleteClassAsync);
    setSelectedIds([]);
    setIsBulkRunning(false);
    showBulkResult({ actionLabel: "Delete", success, failed, skipped });
  };

  const handlePageChange = (page) => {
    if (page < 1 || page > totalPages) return;
    setPaginationConfig((prev) => ({ ...prev, pageNumber: page }));
  };

  const toggleAll = (checked) => {
    if (checked) {
      setSelectedIds((prev) => Array.from(new Set([...prev, ...docs.map((row) => row._id)])));
    } else {
      const pageIds = new Set(docs.map((row) => row._id));
      setSelectedIds((prev) => prev.filter((id) => !pageIds.has(id)));
    }
  };

  const toggleRow = (id, checked) => {
    setSelectedIds((prev) => (checked ? [...prev, id] : prev.filter((item) => item !== id)));
  };

  useEffect(() => {
    setPaginationConfig((prev) => {
      if (prev.keyword === debouncedKeyword) return prev;
      return { ...prev, keyword: debouncedKeyword, pageNumber: 1 };
    });
  }, [debouncedKeyword]);

  useEffect(() => {
    if (!classes) return;
    setPaginationConfig((prev) => ({ ...prev, totalDocs: classes.totalDocs || 0 }));
  }, [classes]);

  useEffect(() => {
    if (!open) return;
    setSelectedStatus(paginationConfig.status || "");
    setSelectedSort(paginationConfig.sort || "newest");
  }, [open, paginationConfig.status, paginationConfig.sort]);

  const rows = useMemo(() => docs, [docs]);

  return (
    <div className="data-page">
      <div className="data-toolbar">
        <div className="data-toolbar-search">
          <label className="data-search">
            <Search size={16} />
            <input
              placeholder="Search here..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            {isFetching && searchTerm ? <span className="data-search-spinner" aria-hidden /> : null}
          </label>
          <button
            type="button"
            className={`data-icon-btn${hasFilters ? " is-active" : ""}`}
            aria-label="Filter"
            onClick={() => setOpen(true)}
          >
            <Filter size={18} />
            {hasFilters ? <span className="data-filter-dot" /> : null}
          </button>

          <DataBulkToolbar
            selectedCount={selectedIds.length}
            onClear={() => setSelectedIds([])}
            disabled={isBulkRunning || isUpdatingStatus}
            actions={[
              { key: "active", label: "Mark as active", onClick: () => handleBulkStatus("ACTIVE") },
              { key: "inactive", label: "Mark as inactive", onClick: () => handleBulkStatus("INACTIVE") },
              { key: "delete", label: "Delete selected", tone: "danger", onClick: handleBulkDelete },
            ]}
          />
        </div>
        <div className="data-toolbar-actions">
          <button type="button" className="data-primary" onClick={() => navigate("/classes/addClass")}>
            <Plus size={18} />
            New Class
          </button>
        </div>
      </div>

      <div className="data-card">
        {isLoading && !classes ? (
          <div style={{ padding: 24 }}>
            <Skeleton active />
          </div>
        ) : rows.length === 0 ? (
          <div className="data-empty">
            <span className="data-empty-icon">
              {hasQuery ? <SearchX size={36} strokeWidth={1.6} /> : <School size={36} strokeWidth={1.6} />}
            </span>
            <h3>{hasQuery ? "No classes found" : "No classes yet"}</h3>
            <p>
              {hasQuery
                ? "No classes match your search or filters. Try a different keyword or clear filters."
                : "Add a class to see it listed here."}
            </p>
          </div>
        ) : (
          <>
            <DataTableCards fetching={isFetching || isUpdatingStatus}>
              <table className="data-table has-row-check">
                <thead>
                  <tr>
                    <th>
                      <input
                        type="checkbox"
                        className="data-check"
                        checked={allSelected}
                        onChange={(e) => toggleAll(e.target.checked)}
                      />
                    </th>
                    <th>Name</th>
                    <th>Section</th>
                    <th>ID</th>
                    <th>Date</th>
                    <th>Teacher</th>
                    <th>Students</th>
                    <th>Batch</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((item) => {
                    const selected = selectedIds.includes(item._id);
                    const homeroom = teacherName(item.teacher);
                    const colorTone = classroomColorTone(item.color);
                    const teacherCount = homeroom ? 1 : 0;
                    const actionItems = [
                      {
                        key: "1",
                        label: <div style={{ padding: "8px 12px" }}>View</div>,
                        onClick: () => navigate(`/classes/${item._id}`),
                      },
                      {
                        key: "2",
                        label: <div style={{ padding: "8px 12px" }}>Edit</div>,
                        onClick: () => navigate(`/classes/edit/${item._id}`),
                      },
                      {
                        key: "3",
                        label: <div style={{ padding: "8px 12px", color: "#e83038" }}>Delete</div>,
                        onClick: () => handleDelete(item),
                      },
                    ];

                    return (
                      <tr key={item._id} className={selected ? "is-selected" : ""}>
                        <td>
                          <input
                            type="checkbox"
                            className="data-check"
                            checked={selected}
                            onChange={(e) => toggleRow(item._id, e.target.checked)}
                          />
                        </td>
                        <td>
                          <div className="data-person">
                            <span className={`data-avatar ${colorTone}`}>
                              {initials(item.classroomName)}
                            </span>
                            <div>
                              <p className="data-name">{item.classroomName || "—"}</p>
                              {item.classroomGrade ? <p className="data-sub">Grade {item.classroomGrade}</p> : null}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={`data-pill ${colorTone}`}>
                            {item.classroomName || "—"}
                          </span>
                        </td>
                        <td className="data-id">#{item.classroomId || "—"}</td>
                        <td>{item.createdAt ? dayjs(item.createdAt).format("MMMM D, YYYY") : "—"}</td>
                        <td>
                          <span
                            className="data-pill data-count-pill is-muted"
                            title={homeroom || "No teacher assigned"}
                          >
                            {teacherCount}
                          </span>
                        </td>
                        <td>
                          <span className="data-pill data-count-pill is-muted">
                            {item.studentCount || 0}
                          </span>
                        </td>
                        <td>{item.classroomBatch || "—"}</td>
                        <td>
                          <Dropdown
                            menu={{
                              items: CLASS_STATUSES.map((option) => ({
                                key: option.value,
                                label: (
                                  <div className="data-status-option">
                                    <span className={`data-status-dot ${statusTone(option.value)}`} />
                                    {option.label}
                                    {item.status === option.value ? <Check size={14} /> : null}
                                  </div>
                                ),
                                onClick: () => handleStatus(item._id, option.value, item.status),
                              })),
                            }}
                            trigger={["click"]}
                            placement="bottomLeft"
                          >
                            <button
                              type="button"
                              className={`data-pill data-status-trigger ${statusTone(item.status)}`}
                              aria-label="Change status"
                            >
                              {statusLabel(item.status)}
                              <ChevronDown size={12} />
                            </button>
                          </Dropdown>
                        </td>
                        <td>
                          <Dropdown menu={{ items: actionItems }} trigger={["click"]} placement="bottomRight">
                            <button type="button" className="data-action" aria-label="More actions">
                              <MoreHorizontal size={20} />
                            </button>
                          </Dropdown>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </DataTableCards>

            <div className="data-pager">
              <p className="data-pager-text">
                Showing {from}-{to} from {totalDocs} data
              </p>
              <div className="data-pages">
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage <= 1}
                  onClick={() => handlePageChange(currentPage - 1)}
                  aria-label="Previous page"
                >
                  <ChevronLeft size={18} />
                </button>
                {pageWindow(currentPage, totalPages).map((page) => (
                  <button
                    key={page}
                    type="button"
                    className={`data-page-btn${page === currentPage ? " is-active" : ""}`}
                    onClick={() => handlePageChange(page)}
                  >
                    {page}
                  </button>
                ))}
                <button
                  type="button"
                  className="data-page-btn is-nav"
                  disabled={currentPage >= totalPages}
                  onClick={() => handlePageChange(currentPage + 1)}
                  aria-label="Next page"
                >
                  <ChevronRight size={18} />
                </button>
              </div>
            </div>
          </>
        )}
      </div>

      <Drawer
        open={open}
        onClose={() => setOpen(false)}
        placement="right"
        title="Filter"
        width={400}
        footer={
          <>
            <button type="button" className="bw-drawer-btn is-ghost" onClick={resetFilter}>
              Clear All
            </button>
            <button type="button" className="bw-drawer-btn is-primary" onClick={applyFilter}>
              Apply
            </button>
          </>
        }
      >
        <div className="bw-filter-field">
          <p className="bw-filter-label">Sort by</p>
          <FilterSelect
            value={selectedSort}
            onChange={setSelectedSort}
            placeholder="Select order"
            options={[
              { value: "newest", label: "Newest" },
              { value: "oldest", label: "Oldest" },
            ]}
          />
        </div>
        <div className="bw-filter-field">
          <p className="bw-filter-label">Status</p>
          <FilterSelect
            multiple
            value={selectedStatus}
            onChange={setSelectedStatus}
            placeholder="All statuses"
            options={[
              { value: "", label: "All statuses" },
              { value: "ACTIVE", label: "Active" },
              { value: "INACTIVE", label: "Inactive" },
            ]}
          />
        </div>
      </Drawer>
    </div>
  );
}

export default ClassManagement;
